import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import notificationService, { PushSubscriptionData } from '../utils/notificationService';
import apiService from '../services/api';

interface NotificationContextType {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  requestPermission: () => Promise<void>;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  testNotification: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [isSupported] = useState<boolean>(notificationService.isSupported());
  const [permission, setPermission] = useState<NotificationPermission>(
    notificationService.getPermissionStatus()
  );
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Check subscription status on mount and when user changes
  useEffect(() => {
    if (isAuthenticated && isSupported) {
      checkSubscriptionStatus();
    }
  }, [isAuthenticated, isSupported]);

  // Update permission status when it changes
  useEffect(() => {
    const updatePermission = () => {
      setPermission(notificationService.getPermissionStatus());
    };

    // Check permission periodically
    const interval = setInterval(updatePermission, 1000);
    return () => clearInterval(interval);
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const subscription = await notificationService.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setIsSubscribed(false);
    }
  };

  const requestPermission = async (): Promise<void> => {
    if (!isSupported) {
      setError('Notificações push não são suportadas neste navegador');
      throw new Error('Push notifications not supported');
    }

    try {
      setIsLoading(true);
      setError(null);

      const newPermission = await notificationService.requestPermission();
      setPermission(newPermission);

      if (newPermission !== 'granted') {
        setError('Permissão para notificações negada');
        throw new Error('Permission denied');
      }
    } catch (error: any) {
      console.error('Error requesting permission:', error);
      setError(error.message || 'Erro ao solicitar permissão para notificações');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const subscribe = async (): Promise<void> => {
    if (!isSupported) {
      setError('Notificações push não são suportadas neste navegador');
      throw new Error('Push notifications not supported');
    }

    if (!isAuthenticated || !user) {
      setError('É necessário estar autenticado para subscrever notificações');
      throw new Error('User must be authenticated');
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get VAPID public key from backend (optional, depending on implementation)
      try {
        const vapidKey = await apiService.getVapidPublicKey();
        notificationService.setVapidPublicKey(vapidKey);
      } catch (error) {
        console.warn('VAPID key not available - backend push notifications not configured');
        throw new Error('Push notifications are not available at this time');
      }

      // Subscribe to push notifications
      const subscription = await notificationService.subscribe();

      // Send subscription to backend
      try {
        await apiService.subscribeToPushNotifications({
          ...subscription,
          user_id: user.id,
          restaurant_id: user.restaurante_id,
        });
      } catch (error) {
        // If backend endpoint doesn't exist, still allow local subscription for future use
        console.warn('Backend subscription endpoint not available:', error);
        throw new Error('Unable to register push notifications with server');
      }

      setIsSubscribed(true);
      
      // Show success notification
      await notificationService.showNotification(
        'Notificações Ativadas! 🔔',
        {
          body: 'Você receberá notificações quando uma reserva for confirmada.',
          tag: 'notification-enabled',
        }
      );
    } catch (error: any) {
      console.error('Error subscribing to notifications:', error);
      setError(error.message || 'Erro ao subscrever notificações');
      setIsSubscribed(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribe = async (): Promise<void> => {
    if (!isSupported) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const subscription = await notificationService.getSubscription();
      
      if (subscription) {
        // Unsubscribe from backend
        try {
          await apiService.unsubscribeFromPushNotifications(subscription.endpoint);
        } catch (error) {
          console.warn('Backend unsubscribe endpoint not available:', error);
          // Continue with local unsubscribe even if backend fails
        }
        
        // Unsubscribe locally
        await notificationService.unsubscribe();
      }

      setIsSubscribed(false);
    } catch (error: any) {
      console.error('Error unsubscribing from notifications:', error);
      setError(error.message || 'Erro ao cancelar subscrição de notificações');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const testNotification = async (): Promise<void> => {
    try {
      await notificationService.showNotification(
        'Teste de Notificação 🔔',
        {
          body: 'Esta é uma notificação de teste. Se você vê isto, as notificações estão funcionando!',
          tag: 'test-notification',
          requireInteraction: false,
        }
      );
    } catch (error: any) {
      console.error('Error showing test notification:', error);
      setError(error.message || 'Erro ao mostrar notificação de teste');
      throw error;
    }
  };

  const value: NotificationContextType = {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    testNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

